import React, {
  FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Link, NavLink } from 'react-router-dom';
import cn from 'classnames';

import {
  ambassadorIcon, eclipseDriveIcon, eclipseVerticalLogoIcon, ekiIcon, logoutIcon,
  ticketIcon, 
} from 'assets/images';
import { PathName, ScreenWidth } from 'appConstants';
import { useScreenWidth, useCountdown } from 'hooks';
import { ModalContext } from 'context';
import { profileSelectors } from 'store/profile/selectors';
import { authSignOut } from 'store/auth/actionCreators';
import { claimCashback } from 'store/profile/actionCreators';

import { 
  TermsWithdrawModal, 
  // VerificationAlertWithdrawModal,
} from 'modals';
import {
  Image,
  Button,
  CopyText,
  LangMenu,
} from 'components';

import { LinksSidebar } from './LinksSidebar';

import styles from './styles.module.scss';

interface ISidebar {
  onBurgerChange?: () => void;
}

export const Sidebar:FC<ISidebar> = memo(({ onBurgerChange }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openJsxModal } = useContext(ModalContext);
  const trans = useTranslation('main').t;
  const isTablet = useScreenWidth(ScreenWidth.tablet);
  const {
    status, balance, id, ekiQuantity, raffleTickets, challenge, isLoading,
  } = useSelector(profileSelectors.getState);
  const isCompany = useSelector(profileSelectors.getProp('isCompany'));
  const companyName = useSelector(profileSelectors.getProp('companyName'));
  const tin = useSelector(profileSelectors.getProp('tin'));
  const [currentProgress, setCurrentProgress] = useState<number>(5);
  const [fillWidth, setFillWidth] = useState(0);
  const [isCashbackComplete, setIsCashbackComplete] = useState(false);

  const {
    days, hours, minutes, seconds, isExpired,
  } = useCountdown(challenge?.start_at, challenge?.expires_at);

  useEffect(() => {
    if (challenge) {
      setFillWidth((challenge.sales_count / challenge.required_sales) * 100);
      setCurrentProgress(challenge.sales_count);
      const cashbackComplete = challenge.sales_count === challenge.required_sales;
      setIsCashbackComplete(cashbackComplete);
    }
  }, [challenge]);

  const handleWithdrawClick = useCallback(() => {
    if (isCompany) {
      if(companyName && tin) {
        openJsxModal(<TermsWithdrawModal userType={7} />);
      } else {
        openJsxModal(<TermsWithdrawModal userType={8} />);
      }
    } else {
      openJsxModal(<TermsWithdrawModal userType={6} />);
    }
  }, [companyName, isCompany, openJsxModal, tin]);

  const onNavigateHome = useCallback(() => {
    navigate(PathName.Home);
  }, [navigate]);

  const onClickSignOut = useCallback(() => {
    dispatch(authSignOut({
      onCallback: onNavigateHome,
    }));
  }, [
    dispatch,
    onNavigateHome,
  ]);

  const handleClaimCashback = useCallback(() => {
    if(challenge && isCashbackComplete && challenge.temp_balance === '720.00' && challenge.status === 'completed') {
      dispatch(claimCashback());
    }
  }, [challenge, dispatch, isCashbackComplete]);

  return (
    <div className={styles.sidebar}>
      <Link
        to={PathName.Profile}
        className={styles.header_logo_container}
      >
        <Image
          url={eclipseVerticalLogoIcon}
          className={cn(styles.sidebar_logo, {
            [styles.sidebar_logo_laptop]: isTablet,
          })}
        />
      </Link>
      <div>
        {status && (
        <div className={styles.sidebar_ambassador}>
          <Image url={ambassadorIcon} />
          <span>{trans('Is_Ambassador_')}</span>
        </div>
        )}
        <div className={styles.sidebar_balance}>
          {ekiQuantity > 0 && (
          <div className={styles.sidebar_eki}>
            <Image url={ekiIcon} />
            {id === 2 || id === 3 || id === 302 ? (
              <span>
                {ekiQuantity + 9}
                {' '}
                EKI
              </span>
            ) : (
              <span>
                {ekiQuantity}
                {' '}
                EKI
              </span>
            )}
          </div>
          )}
          <div className={styles.sidebar_balance_text}>
            $
            {' '}
            {balance}
          </div>
          <Button
            onClick={handleWithdrawClick}
            color="blue"
          >
            {trans('Withdraw_')}
          </Button>
        </div>
        {raffleTickets.length > 0 && raffleTickets.map((ticket) => (
          <div
            className={styles.sidebar_ticket}
            key={ticket}
          >
            <Button
              color="pink"
            >
              <Image url={ticketIcon} />
              <span>
                №
                {' '}
                {ticket}
              </span>
            </Button>
          </div>
        ))}
        <div className={styles.sidebar_eclipse_drive}>
          <NavLink
            onClick={onBurgerChange}
            to={PathName.EclipseDriveAuth}
            className={styles.sidebar_eclipse_drive_button}
          >
            <span>Eclipse Drive</span>
            <Image
              url={eclipseDriveIcon}
              className={styles.sidebar_eclipse_drive_button_icon}
            />
          </NavLink>
        </div>
        {challenge && (
        <div className={[styles.sidebar_balance, styles.sidebar_cashback].join(' ')}>
          <p>
            {trans('Cashback_challenge_')}
            {' '}
            &nbsp; 
            {' '}
            {trans('Temporary_balance_')}
          </p>
          <span>
            {isExpired ? (
              <span>{trans('Time_is_up_')}</span>
            ) : (
              <div>
                <span>{days}</span>
                :
                <span>{hours}</span>
                :
                <span>{minutes}</span>
                :
                <span>{seconds}</span>
              </div>
            )}
          </span>
          <div className={styles.sidebar_cashback_calculator}>
            <div className={styles.sidebar_cashback_calculator_item}>
              <p>{trans('Supercases_')}</p>
              <span>
                {currentProgress}
                /
                {challenge.required_sales}
              </span>
            </div>
            <div className={styles.sidebar_cashback_calculator_item}>
              <p>{trans('Sum_')}</p>
              <span>
                $
                {challenge.temp_balance}
              </span>
            </div>
          </div>
          <div className={styles.sidebar_progress}>
            <div
              className={styles.sidebar_progress_fill}
              style={{ width: `${fillWidth}%` }}
            />
          </div>
          <Button
            color="pink"
            disabled={!isCashbackComplete || isLoading}
            onClick={handleClaimCashback}
          >
            {!isCashbackComplete ? trans('Blocked_') : trans('Claim_')}
          </Button>
        </div>
        )}
        {!!id && (
          <div className={styles.sidebar_id}>
            ID:
            {' '}
            <CopyText
              text={id}
            />
          </div>
        )}
        <LinksSidebar onBurgerChange={onBurgerChange} />
        <div className={styles.sidebar_bottom}>
          <LangMenu
            isLabel
          />
          {!isTablet && (
          <button
            type="button"
            onClick={onClickSignOut}
            className={styles.sidebar_menu}
          >
            <Image url={logoutIcon} />
            <div className={styles.sidebar_menu_text}>
              {trans('Logout_')}
            </div>
          </button>
          )}
        </div>
      </div>
    </div>
  );
});
